import { Dispatch, SetStateAction } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform, Alert } from 'react-native';
import * as Haptics from 'expo-haptics';
import * as Location from 'expo-location';
import BackgroundGeolocation from 'react-native-background-geolocation';
import { initializeRecordingSession, loadRecordingSnapshot, clearRecordingSession, RECORDING_STORAGE_KEYS } from './recordingSession';
import i18n from '@/i18n';
import { resolveInitialRecordingCoordinate } from './recordingFlow';
import { Coordinate, Trail, TrailPoint } from '@/types/trail';
import { checkBatteryOptimizationStatus, showBatteryOptimizationAlert, BatteryCheckResult } from './batteryOptimization';
import { diag, reportTrackingIssue } from './diagnostics';
import { resetTrailHealth } from './trailHealth';
import { logEvent } from '@/lib/analytics';

type LoadStateParams = {
  setCoordinates: (c: TrailPoint[]) => void;
  setIsRecording: (b: boolean) => void;
  setDuration: (n: number) => void;
  setMaxElevation: (n: number) => void;
  setMaxSpeed: (n: number) => void;
  setSniffTime?: (n: number) => void;
};

export async function loadRecordingState(params: LoadStateParams) {
  const { setCoordinates, setIsRecording, setDuration, setMaxElevation, setMaxSpeed, setSniffTime } = params;
  try {
    const snapshot = await loadRecordingSnapshot();

    if (!snapshot.hasSession) {
      await AsyncStorage.removeItem(RECORDING_STORAGE_KEYS.COORDINATES);
      setCoordinates([]);
      setIsRecording(false);
      setDuration(0);
    } else {
      setIsRecording(true);
      setCoordinates(snapshot.coordinates);
      setDuration(snapshot.duration);
    }

    setMaxElevation(snapshot.maxElevation);
    setMaxSpeed(snapshot.maxSpeed);
    if (setSniffTime) setSniffTime(snapshot.sniffTime);
  } catch (err) {
    console.error('Error loading recording state (helper):', err);
  }
}

type RequestPermsParams = {
  bgReadyRef: { current: boolean };
  setHasPermission: (b: boolean) => void;
  setIsLoadingPermission: (b: boolean) => void;
  setCurrentLocation?: (c: Coordinate | null) => void;
  requestBgPermissionAndInitialLocation: (timeout: number) => Promise<any>;
};

export async function requestPermissions(params: RequestPermsParams) {
  const { bgReadyRef, setHasPermission, setIsLoadingPermission, setCurrentLocation, requestBgPermissionAndInitialLocation } = params;
  try {
    if (!bgReadyRef.current) {
      setHasPermission(false);
      setIsLoadingPermission(false);
      return;
    }

    // Fast display seed: populate currentLocation from the OS last-known fix
    // so the map mounts immediately instead of showing a black screen during
    // the up-to-30s live GPS search. The slow live fix (and the BG onLocation
    // listener) refines it afterwards.
    try {
      const last = await Location.getLastKnownPositionAsync();
      if (last?.coords && setCurrentLocation) {
        setCurrentLocation({ latitude: last.coords.latitude, longitude: last.coords.longitude });
      }
    } catch (err) {
      console.warn('getLastKnownPositionAsync failed (helper)', err);
    }

    try {
      const initial = await requestBgPermissionAndInitialLocation(30);
      if (initial.coordinate && setCurrentLocation) setCurrentLocation(initial.coordinate);
      setHasPermission(true);
    } catch (err) {
      console.warn('BG getCurrentPosition failed (helper)', err);
      setHasPermission(true);
    }
  } catch (err) {
    console.error('Permission error (helper):', err);
    setHasPermission(false);
  } finally {
    setIsLoadingPermission(false);
  }
}

/**
 * Check battery optimization status and show a warning to the user
 * if their device may kill background tracking.  Returns true if it's
 * safe to proceed with recording, false if the user cancelled.
 *
 * This is the key defence against Samsung / OEM app-killing: we detect
 * when the app is NOT whitelisted from battery optimization and guide
 * the user through fixing it BEFORE they start their walk.
 */
export async function checkAndWarnBatteryOptimization(): Promise<boolean> {
  if (Platform.OS !== 'android') return true;

  try {
    const result = await checkBatteryOptimizationStatus();

    if (result.isOptimized || result.needsOEMConfig) {
      // User's app is subject to battery optimization — show guidance
      const userActed = await showBatteryOptimizationAlert(result);
      // Always allow proceeding — the user may want to start anyway
      // and fix settings later.  But we've at least informed them.
      return true;
    }

    return true;
  } catch (err) {
    console.warn('Battery optimization check failed:', err);
    // Don't block recording if the check itself fails
    return true;
  }
}

type StartParams = {
  setIsRecording: (b: boolean) => void;
  // Functional updater allowed: the post-start GPS seed must be able to
  // skip clobbering live points that arrived while it was resolving.
  setCoordinates: Dispatch<SetStateAction<TrailPoint[]>>;
  setDuration: (n: number) => void;
  setMaxElevation: (n: number) => void;
  setMaxSpeed: (n: number) => void;
  setCurrentLocation?: (c: Coordinate | null) => void;
  bgReadyRef: { current: boolean };
  resolveInitialRecordingCoordinate: typeof resolveInitialRecordingCoordinate;
  timerRef: { current: ReturnType<typeof setInterval> | null };
  setStartLocation?: (s: any) => void;
  /** If true, run the non-blocking post-start battery guidance. */
  checkBattery?: boolean;
  /** Extra properties for the walk_started analytics event (e.g. follow_mode). */
  walkEventProps?: Record<string, any>;
};

/** Returns null on success, or a failure-reason string if the walk was torn down. */
export async function startRecordingShared(params: StartParams): Promise<string | null> {
  const { setIsRecording, setCoordinates, setDuration, setMaxElevation, setMaxSpeed, setCurrentLocation, bgReadyRef, resolveInitialRecordingCoordinate, timerRef, setStartLocation, checkBattery = true } = params;

  // ── Battery optimisation guidance runs POST-start ──────────────────
  // Never await OEM firmware calls before starting: on Samsung One UI they
  // can hang forever, which silently dead-ended the Start button (tap →
  // nothing happens, no alert, no recording). Tracking starts first; the
  // guidance alert fires best-effort afterwards, below.
  diag('start_tapped');

  if (true) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Heavy);
  setIsRecording(true);
  setCoordinates([]);
  setDuration(0);
  setMaxElevation(0);
  setMaxSpeed(0);
  // Log here, once, for every walk (record tab + follow flow).
  logEvent('walk_started', params.walkEventProps ?? {});

  try {
    const startTime = Date.now();
    await initializeRecordingSession(startTime);

    // Fresh baseline for the trail-health watchdog (notification on GPS stall)
    resetTrailHealth();

    // ── Background location permission (Android incremental dialog) ──
    // 'Any' never showed this on fresh installs; One UI throttles a
    // while-in-use location FGS with the screen off, so ask for
    // "Allow all the time" while we're still in the foreground tap.
    // Instant no-op when already granted; non-fatal when denied.
    if (Platform.OS === 'android') {
      try {
        const bg = await Location.getBackgroundPermissionsAsync();
        if (bg.status !== 'granted') {
          const req = await Location.requestBackgroundPermissionsAsync();
          diag('bg_perm_increment_request', req.status);
        } else {
          diag('bg_perm_granted');
        }
      } catch (e) {
        diag('bg_perm_error', String((e as any)?.message ?? e));
      }
    }

    if (setStartLocation) setStartLocation(null);

    // Start tracking FIRST: a slow/hung getCurrentPosition (up to 30s, or never on
    // throttled Samsung OEM firmware) must never delay or block the start of tracking.
    try {
      if (!bgReadyRef.current) {
        const startWait = Date.now();
        while (!bgReadyRef.current && Date.now() - startWait < 5000) {
          await new Promise((res) => setTimeout(res, 100));
        }
      }

      await BackgroundGeolocation.start();
      // BE AWARE: Force the background geolocation plugin into the "moving" state.
      // This is especially important for testing on iOS simulators (like "City Run")
      // because simulators do not simulate the CMMotionActivity (motion sensor) events 
      // that the plugin normally relies on to wake up from its stationary state.
      await BackgroundGeolocation.changePace(true);

      // Verify the service actually came up: OEM firmware (Samsung One UI / Android
      // 16+) can silently refuse the location foreground-service, leaving the UI in
      // the recording state with a dead tracker ("button didn't start recording").
      const startState = await BackgroundGeolocation.getState();
      if (!startState.enabled) throw new Error('tracking service not enabled after start()');
      diag('start_ok');
    } catch (bgErr) {
      const failMsg = bgErr instanceof Error ? bgErr.message : String(bgErr ?? 'unknown');
      console.warn('BackgroundGeolocation.start() failed (helper)', bgErr);
      diag('start_fail', failMsg);
      // One retry after a short delay — transient ForegroundServiceStartNotAllowed /
      // did-not-start-in-time races on Samsung firmware often resolve within a second.
      let started = false;
      try {
        await new Promise((res) => setTimeout(res, 1500));
        await BackgroundGeolocation.start();
        await BackgroundGeolocation.changePace(true);
        const retryState = await BackgroundGeolocation.getState();
        started = !!retryState.enabled;
        diag(started ? 'start_retry_ok' : 'start_retry_fail');
      } catch (retryErr) {
        console.warn('[BG] retry after failed start also failed', retryErr);
      }
      if (!started) {
        // Tear the half-started session down so the UI shows Start again, and tell
        // the user what happened instead of failing silently.
        setIsRecording(false);
        try { await clearRecordingSession(); } catch (e) { /* ignore */ }
        const rawMsg = bgErr instanceof Error ? bgErr.message : String(bgErr ?? 'unknown error');
        const errMsg = rawMsg.length > 120 ? `${rawMsg.slice(0, 120)}…` : rawMsg;
        diag('start_failed_alert', errMsg);
        // Ship the dump so failed starts land in the operator's dashboard too.
        void reportTrackingIssue(`start_failed: ${errMsg}`);
        // Alerts are best-effort: firmware that eats dialogs must not crash
        // the flow — the zero-GPS banner is the visible signal regardless.
        try {
          Alert.alert(
            i18n.t('record.startFailedTitle'),
            i18n.t('record.startFailedMessage', { error: errMsg }),
          );
        } catch (e) { /* dialog failed; diagnostics capture this either way */ }
        return errMsg;
      }
    }

    // ── Post-start battery guidance (non-blocking, fire-and-forget) ───
    // Warns about Samsung/Xiaomi battery optimisation AFTER the walk has
    // started, so firmware quirks can never block recording.
    if (Platform.OS === 'android' && checkBattery) {
      void (async () => {
        try {
          const result = await checkBatteryOptimizationStatus();
          diag('battery_check', `oem=${result.oem} optimized=${result.isOptimized}`);
          if (result.isOptimized || result.needsOEMConfig) {
            showBatteryOptimizationAlert(result);
          }
        } catch (e) {
          diag('battery_check_error', String((e as any)?.message ?? e));
        }
      })();
    }

    // seed initial coordinate if available — after start, so a 30s GPS wait can
    // only delay the first pin, never the tracking itself (onLocation still
    // appends live points while this runs).  Seed is non-destructive: if live
    // points already arrived, leave them; a coarser-worse seed never clobbers.
    if (bgReadyRef.current && resolveInitialRecordingCoordinate) {
      try {
        const initial = await resolveInitialRecordingCoordinate(undefined, 30);
        if (initial?.coordinate) {
          const seed: TrailPoint = { ...initial.coordinate, recordedAt: Date.now() };
          setCoordinates((prev) => (prev.length === 0 ? [seed] : prev));
          const existing = await AsyncStorage.getItem(RECORDING_STORAGE_KEYS.COORDINATES);
          if (!existing) {
            await AsyncStorage.setItem(RECORDING_STORAGE_KEYS.COORDINATES, JSON.stringify([seed]));
          }
          if (setCurrentLocation) setCurrentLocation(initial.coordinate);
        }
      } catch (e) { /* ignore */ }
    }

    timerRef.current = setInterval(async () => {
      try {
        const startTimeStr = await AsyncStorage.getItem(RECORDING_STORAGE_KEYS.START_TIME);
        if (startTimeStr) {
          const startTime = parseInt(startTimeStr);
          const elapsed = Math.floor((Date.now() - startTime) / 1000);
          setDuration(elapsed);
        }
      } catch (err) { /* swallow */ }
    }, 1000);
  } catch (err) {
    console.error('Recording error (helper):', err);
    setIsRecording(false);
    return err instanceof Error ? err.message : String(err ?? 'start failed');
  }
  return null;
}

type StopParams = {
  timerRef: { current: ReturnType<typeof setInterval> | null };
  coordinates: TrailPoint[];
  maxElevation: number;
  maxSpeed: number;
  duration: number;
};

export async function stopRecordingShared(params: StopParams) {
  const { timerRef, coordinates, maxElevation, maxSpeed, duration } = params;
  try {
    await BackgroundGeolocation.stop();
  } catch (err) {
    console.warn('BG stop error (helper)', err);
  }
  // Walk over: no more stall warnings from the health watchdog
  resetTrailHealth();

  if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }

  const snapshot = await loadRecordingSnapshot({
    fallbackCoordinates: coordinates,
    fallbackMaxElevation: maxElevation,
    fallbackMaxSpeed: maxSpeed,
    fallbackDuration: duration,
  });

  // ⚠ Reporting backstop for the frozen-process case: the stall watchdog only
  // runs while the app process is alive — if One UI froze the app mid-walk it
  // can never fire. Here the user is actively pressing Stop, so the process is
  // guaranteed alive: if the walk ran long but recorded almost nothing, report
  // it so it lands in the operator's dashboard.
  const walkDur = Number(snapshot.duration ?? duration ?? 0);
  const walkPts = Array.isArray(snapshot.coordinates) ? snapshot.coordinates.length : 0;
  if (walkDur >= 300 && walkPts < 3) {
    diag('empty_walk_completed', `dur=${walkDur}s pts=${walkPts}`);
    void reportTrackingIssue(`empty_walk_completed: dur=${walkDur}s pts=${walkPts}`);
  }

  return snapshot;
}